import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from '@app/modules/core/store/app.state';
import { LoggedInModuleType } from '@app/modules/shared/interfaces/logged-in-module.type';
import { LoginResponse } from '@app/modules/shared/interfaces/login-response.type';
import { HelperService } from '@app/services/helper.service';
import { LoggingService, LogLevel } from '@app/services/logging.service';
import { Store } from '@ngrx/store';
import { Observable, finalize, map, shareReplay } from 'rxjs';
import { environment as env } from 'environments/environment';
import * as AuthActions from '../../modules/core/store/actions/auth.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@app/services/auth.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { confirmPasswordValidator } from '@app/modules/shared/validators/confirm-password.validator';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
    standalone: false
})
export class ResetPasswordComponent {
  static readonly COMPONENT_NAME = 'ResetPasswordComponent';
  loggingService = inject(LoggingService);

  errorMessage = '';
  loaded: any;
  s3Url = '';
  isMobile: boolean = false;
  loginResponse!: LoginResponse;
  loading = true;
  token!: string;
  tokenVerified = false;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
    .pipe(
      map(result => {
        console.log("== result ==", result);
        return result.matches;
      }),
      shareReplay()
    );


  formLoading: boolean = false;
  forgotPasswordForm = new FormGroup(
    {
      password: new FormControl('', Validators.required),
      confirmPassword: new FormControl('', Validators.required),
    },
    { validators: confirmPasswordValidator('password', 'confirmPassword') }
  );

  validationMessages = {
    password: [
      { type: 'required', message: 'Password is required' },
    ],
    confirmPassword: [
      { type: 'required', message: 'Confirm Password is required' },
      { type: 'notSame', message: 'Passwords do not match' },
    ],
  }


  constructor(
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<AppState>,
    private helper: HelperService,
    private authService: AuthService
  ) {
    this.s3Url = env?.bucketUrl;
  }

  ngOnInit(): void {
    this.isHandset$.subscribe(r => {
      console.log('IS HANDSET:', r);
      this.isMobile = r;
    });

    this.route.params.subscribe((qp) => {
      if (qp['token'] && qp['token'] != '') {
        this.token = qp['token'];
        this.verifyForgetPasswordToken();
        this.loggingService.log(LogLevel.DEBUG, ResetPasswordComponent.COMPONENT_NAME, 'ngOnInit: token:', this.token);
      } else {
        this.router.navigateByUrl('/login');
      }
    });
  }

  verifyForgetPasswordToken() {
    this.loading = true;
    const observer = {
      next: (res: any) => {
        this.loggingService.log(LogLevel.DEBUG, ResetPasswordComponent.COMPONENT_NAME, 'verifyForgetPasswordToken: res:', res);
        this.tokenVerified = true;
      },
      error: (err: HttpErrorResponse) => {
        this.loggingService.log(LogLevel.DEBUG, ResetPasswordComponent.COMPONENT_NAME, 'verifyForgetPasswordToken: err:', err);

        this.errorMessage = err?.error?.message ?? 'Oops! Something went wrong. Please try again.'
        this.loading = false;
        this.helper.showHttpErrorMsg(err);
      },
      complete: () => {
        this.loading = false;
      }
    };
    this.authService.verifyForgotPasswordResetToken({ token: this.token }).subscribe(observer);
  }


  onSubmit() {
    this.loggingService.log(LogLevel.DEBUG, ResetPasswordComponent.COMPONENT_NAME, 'onSubmit:', this.forgotPasswordForm.value);
    this.forgotPasswordForm.markAllAsTouched();

    if (this.forgotPasswordForm.valid) {
      this.formLoading = true;

      let payload = this.forgotPasswordForm.value as Partial<{ password: string; }>;
      this.authService.updateForgottenPassword({ password: payload.password, token: this.token }).pipe(
        finalize(() => {
          this.formLoading = false;
        })
      ).subscribe({
        next: (response: any) => {
          this.loggingService.log(LogLevel.DEBUG, ResetPasswordComponent.COMPONENT_NAME, 'forgotPassword: response:', response);
          this.helper.showSuccessMsg(response.message, 'Success');
          this.router.navigateByUrl('/login');
        },
        error: (err: HttpErrorResponse) => {
          this.helper.showHttpErrorMsg(err);
        },
        complete: () => {
          this.formLoading = false;
        }
      });
    } else {
      this.loggingService.log(LogLevel.DEBUG, ResetPasswordComponent.COMPONENT_NAME, 'this.forgotPasswordForm.valid', this.forgotPasswordForm.valid);
    }

  }

}
